import { RotateCcw } from 'lucide-react';
import type { ReactNode } from 'react';
import { PageShellHeaderSlot } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { Button } from '@/components/ui/button';
import { DashboardSwitcher } from './dashboard-switcher';
import { PeriodPicker } from './period-picker';
import type { DashboardDef } from './registry';

interface Props {
  dashboard: DashboardDef;
  period: string;
  onPeriodChange: (period: string) => void;
  /**
   * Live figures for the header — period, headcount, total hours. Deliberately
   * separate from `dashboard.description`, which is static registry copy for
   * the switcher: the two numbers that frame the whole page are worth more
   * here than a sentence explaining what a dashboard is.
   */
  summary?: ReactNode;
  /** Restore the default tile arrangement; the button hides when nothing moved. */
  onResetLayout?: () => void;
  layoutDirty?: boolean;
  children: ReactNode;
}

/**
 * Page chrome shared by every dashboard: title, dashboard switcher, reporting
 * period, and the reset-layout affordance.
 *
 * Rendered *by* the dashboard component rather than by the route, because the
 * reset action belongs to whoever owns the layout state — which is the
 * dashboard, since each one has its own default arrangement.
 */
export function DashboardShell({
  dashboard,
  period,
  summary,
  onPeriodChange,
  onResetLayout,
  layoutDirty = false,
  children,
}: Props) {
  return (
    <PageShell>
      <PageShellHeaderSlot>
        <div className="min-w-0">
          <h1 className="font-semibold text-2xl">{dashboard.title}</h1>
          <p className="text-muted-foreground text-sm">{summary ?? dashboard.description}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {onResetLayout && layoutDirty ? (
            <Button type="button" variant="ghost" size="sm" onClick={onResetLayout}>
              <RotateCcw className="size-3.5" aria-hidden />
              Reset layout
            </Button>
          ) : null}
          {/* The picker alone doesn't say what it selects; the label is the
              difference between "Jul 2026" meaning something and floating. */}
          <span className="font-medium text-[10px] text-muted-foreground uppercase tracking-[0.09em]">
            Month
          </span>
          <PeriodPicker value={period} onChange={onPeriodChange} />
          <DashboardSwitcher current={dashboard.id} />
        </div>
      </PageShellHeaderSlot>
      {children}
    </PageShell>
  );
}
